import ApiService from '@/services/ApiService'
import Api from '@/constants/api'
import escapeStringRegexp from 'escape-string-regexp'
import _sortBy from 'lodash/sortBy'
import _uniq from 'lodash/uniq'
import _flatten from 'lodash/flatten'

const communitiesService = new ApiService(Api.COMMUNITIES, 'community')

const createCommunitiesModule = () => ({
  namespaced: true,
  state: {
    page: 1,
    pages: 1,
    pageSize: 115,
    total: 0,
    communities: [],
    armedField: null,
    isLoading: false,
    activeDomain: null,
    activeCommunityType: null,
    activeSkill: null,
    skillSearch: null,
    viewSkillSearchResult: null
  },
  actions: {
    getCommunities: async ({ commit, state }) => {
      try {
        commit('SET_IS_LOADING', true)
        const data = await communitiesService.getItems(state.pageSize, state.page)

        commit('SET_COMMUNITIES', { communities: data.communities })
        commit('SET_PAGES', { pages: data.pages })
        commit('SET_TOTAL', { total: data.total })
        commit('SET_IS_LOADING', false)
      } catch (err) {
        console.error('getCommunities: ', err.message || err)
      }
    },
    deleteCommunities: async ({ dispatch }, id) => {
      await communitiesService.deleteItem(id)
      await dispatch('getCommunities')
    },
    setPage: ({ commit, dispatch }, page) => {
      commit('SET_PAGE', { page })
      dispatch('getCommunities')
    },
    setPageSize: ({ commit, dispatch }, pageSize) => {
      commit('SET_PAGE_SIZE', { pageSize })
    },
    armField: ({ commit }, field) => {
      commit('SET_ARMED_FIELD', { field })
    },
    setActiveDomain: ({ commit }, activeDomain) => {
      commit('SET_ACTIVE_DOMAIN', activeDomain)
    },
    setActiveCommunityType: ({ commit }, activeCommunityType) => {
      commit('SET_ACTIVE_COMMUNITY_TYPE', activeCommunityType)
    },
    loadFilters: ({ commit, rootState }) => {
      if (rootState.route.query.domain) {
        const domainSlug = rootState.route.query.domain
        const domain = rootState.metadata.domains.find((d) => d.slug === domainSlug)

        commit('SET_ACTIVE_DOMAIN', domain)
      }
      if (rootState.route.query.type) {
        commit('SET_ACTIVE_COMMUNITY_TYPE', rootState.route.query.type)
      }
      if (rootState.route.query.skill) {
        commit('SET_ACTIVE_SKILL', rootState.route.query.skill)
      }
    },
    setSkillSearch({ state }, skillSearch) {
      state.skillSearch = skillSearch
    },
    async hideSkillSearchResult({ state }) {
      await new Promise((resolve) => setTimeout(resolve, 200))
      state.viewSkillSearchResult = false
    },
    setActiveSkill({ state }, skill) {
      state.activeSkill = skill
      state.skillSearch = skill
      state.viewSkillSearchResult = false
    },
    setViewSkillSearchResult({ state }, viewSkillSearchResult) {
      state.viewSkillSearchResult = viewSkillSearchResult
    }
  },
  getters: {
    prevDisabled: (state) => state.page === 1,
    nextDisabled(state) {
      return state.pageSize * state.page >= state.total
    },
    armedField: ({ armedField }) => armedField,
    communities: ({ communities }) => communities,
    isLoading: ({ isLoading }) => isLoading,
    communityTypes: ({ communities }) =>
      communities
        .map((c) => c.communityType)
        .filter(
          (communityType, i, arr) =>
            arr.findIndex((currentType) => currentType === communityType) === i
        ),
    skills({ communities }) {
      const skills = communities.filter((c) => c.skills).map((c) => c.skills)

      return _sortBy(_uniq(_flatten(skills)))
    },
    activeDomain: ({ activeDomain }) => activeDomain,
    activeDomainSlug: ({ activeDomain }) => activeDomain?.slug,
    activeSkill: ({ activeSkill }) => activeSkill,
    activeCommunityType: ({ activeCommunityType }) => activeCommunityType,
    communitiesFiltered: ({ communities, activeDomain, activeCommunityType, activeSkill }) =>
      communities.filter(
        (c) =>
          (!activeDomain || c.domain.slug === activeDomain.slug) &&
          (!activeCommunityType || c.communityType === activeCommunityType) &&
          (!activeSkill || c.skills.includes(activeSkill))
      ),
    skillSearch: ({ skillSearch }) => skillSearch,
    viewSkillSearchResult: ({ viewSkillSearchResult }) => viewSkillSearchResult,
    matchedSkills: ({ skillSearch }, getters) => {
      if (skillSearch.length < 1) {
        return []
      }

      const searchRegexp = new RegExp(`^${escapeStringRegexp(skillSearch)}`, 'i')

      return getters.skills.filter((skill) => searchRegexp.test(skill)).slice(0, 6)
    }
  },
  mutations: {
    SET_COMMUNITIES: (state, { communities }) => {
      state.communities.length = 0
      state.communities.push(...communities)
    },
    SET_PAGES: (state, { pages }) => {
      state.pages = pages
    },
    SET_PAGE: (state, { page }) => {
      state.page = page
    },
    SET_PAGE_SIZE: (state, { pageSize }) => {
      state.pageSize = pageSize
    },
    SET_TOTAL: (state, { total }) => {
      state.total = total
    },
    SET_ARMED_FIELD: (state, { field }) => {
      state.armedField = field
    },
    SET_IS_LOADING: (state, isLoading) => {
      state.isLoading = isLoading
    },
    SET_ACTIVE_DOMAIN: (state, activeDomain) => {
      state.activeDomain = activeDomain
    },
    SET_ACTIVE_COMMUNITY_TYPE: (state, activeCommunityType) => {
      state.activeCommunityType = activeCommunityType
    },
    SET_ACTIVE_SKILL: (state, activeSkill) => {
      state.activeSkill = activeSkill
    }
  }
})

export default createCommunitiesModule
